import { UserOrganizationResponse } from '@cdaas/api-clients/dist/asset-user-openapi';

import { NUMBER_OF_PRODUCT_PER_PAGE } from './productUtils';
import { FilterProductsParams } from '../types';
import history from '../history';
import * as H from 'history';
import { useLocation } from 'react-router';
import { useMemo } from 'react';
import { openUrlInNewTab, redirectToUrlInCurrentTab } from './navigationUtils';

const QUERY_PARAM_EXTERNAL_URL = 'ext-url';
const QUERY_PARAM_OPEN_LINK_IN_NEW_TAB = 'open-new-tab';

export const buildQueryString = (filterParams: FilterProductsParams): string => {
  const params: Record<string, string> = {};
  if (filterParams.paging?.currentPage && filterParams.paging?.currentPage > 1) {
    params.page = `${filterParams.paging!.currentPage - 1}`;
  }
  if (filterParams.paging?.numberOfProductsPerPage !== NUMBER_OF_PRODUCT_PER_PAGE) {
    params.size = `${filterParams.paging!.numberOfProductsPerPage}`;
  }
  if (filterParams?.brands?.length) {
    params.brand = `${filterParams.brands.join(',')}`;
  }
  if (filterParams?.textSearch) {
    params.name = `${filterParams.textSearch}`;
  }
  if (filterParams?.sorting?.value) {
    const [sort, direction] = filterParams.sorting.value.split('_');
    params.sort = `${sort}`;
    params.direction = `${direction}`;
  }
  const searchParamsString = new URLSearchParams(params).toString();
  return searchParamsString ? `?${searchParamsString}` : '';
};

export const getTicketURI = (language?: UserOrganizationResponse['locale']['language']) => {
  switch (language?.split('-')[0]) {
    case 'no':
      return process.env.REACT_APP_TICKET_URI_NO;
    case 'se':
      return process.env.REACT_APP_TICKET_URI_SE;
    case 'fi':
      return process.env.REACT_APP_TICKET_URI_FI;
    default:
      return process.env.REACT_APP_TICKET_URI;
  }
};

/**
 * Generic function to update query params in the URL. Does not support values that are arrays.
 * @param pathname pathname as string
 * @param searchParams Record/Object with query params for updating into URL
 * @param replaceOrPush for React router to use replace or push method
 */
export const updateQueryParams = (pathname: string, searchParams: Record<string, string | number | boolean>, replaceOrPush: 'replace' | 'push') => {
  const convertedToStrings: Record<string, string> = {};
  for (const key of Object.keys(searchParams)) {
    convertedToStrings[key] = searchParams[key].toString();
  }
  if (replaceOrPush === 'replace') {
    history.replace({ pathname, search: `?${new URLSearchParams(convertedToStrings).toString()}` });
  } else if (replaceOrPush === 'push') {
    history.push({ pathname, search: `?${new URLSearchParams(convertedToStrings).toString()}` });
  }
};

/**
 * Generic function to get query params from any URL. Does not support params that are arrays.
 * @param searchQuery usually location. Search in URLSearchParams
 * @param paramsConfig configuration for getting and parsing query params
 * @returns Record/Object parsed with given configuration
 */
export const getQueryParams = (
  searchQuery: URLSearchParams,
  paramsConfig: {
    name: string;
    type: 'string' | 'number' | 'boolean';
    defaultValue?: string | number | boolean;
    allowedValues?: string[] | number[];
  }[],
): Record<string, string | number | boolean> => {
  const final: Record<string, string | number | boolean> = {};
  for (const config of paramsConfig) {
    // Get from query params
    let queryParam = searchQuery.get(config.name) ?? config.defaultValue;
    // Convert to number and check
    if (config.type === 'number') {
      queryParam = Number.isInteger(+queryParam!) ? +queryParam! : config.defaultValue;
    }
    // Convert to boolean and check
    if (config.type === 'boolean') {
      queryParam = queryParam === 'true' ? true : queryParam === 'false' ? false : config.defaultValue;
    }
    // Check if exists in allowed values. If exists then push to array and continue iterating
    if (config.allowedValues) {
      // Cast to (string | number)[] so that typescript is happy
      const exists = (config.allowedValues as (string | number)[]).find((value: string | number) => value === queryParam);
      if (exists) final[config.name] = queryParam!;
      continue;
    }
    // Push to array if valid
    if (queryParam !== null && queryParam !== undefined) {
      final[config.name] = queryParam;
    }
  }
  return final;
};

/**
 * Generates a URL which can be used for linking to external URLs in Telia React Component Library menu items.
 * This is a workaround for a bug in the library that prevents using external links in menu items.
 * The generated URL will have 'ext-url' query parameter which will trigger opening the link via App.tsx hook (see openExternalUrlFromQueryParams below).
 * This function respects current location pathname and search parameters, appending search parameters without overwriting existing values.
 * @param locationPathname use location.pathname from react-router
 * @param locationSearchParams use location.search from react-router
 * @param url the external URL to link to
 * @param openInNewTab optional - if true, the link will be opened in a new tab, otherwise in the same tab
 */
export const getExternalLinkForMenuItem = (locationPathname: string, locationSearchParams: string, url: string, openInNewTab = false) => {
  const params = new URLSearchParams(locationSearchParams);
  params.append(QUERY_PARAM_EXTERNAL_URL, url);
  if (openInNewTab) {
    params.append(QUERY_PARAM_OPEN_LINK_IN_NEW_TAB, 'true');
  }
  return `${locationPathname}?${params.toString()}`;
};

/**
 * Workaround for opening external links from header menu items.
 * There is a bug in Telia React Component Library that prevents using external links in menu items.
 * Presence of 'ext-url' query parameter in the URL will trigger opening the link in a new tab.
 * After opening the link, the query parameter is removed from the URL to prevent repeated opening.
 */
export const openExternalUrlFromQueryParams = (locationSearch: string, history: H.History<unknown>) => {
  const query = new URLSearchParams(locationSearch);
  const externalUrl = query.get(QUERY_PARAM_EXTERNAL_URL);
  if (externalUrl) {
    const openLinkInNewTab = query.get(QUERY_PARAM_OPEN_LINK_IN_NEW_TAB) === 'true';
    if (openLinkInNewTab) {
      openUrlInNewTab(externalUrl);
    }
    query.delete(QUERY_PARAM_EXTERNAL_URL);
    query.delete(QUERY_PARAM_OPEN_LINK_IN_NEW_TAB);
    history.replace({ search: query.toString() });
    if (!openLinkInNewTab) {
      redirectToUrlInCurrentTab(externalUrl);
    }
  }
};

/**
 * Custom hook to get query parameters from the current URL.
 */
export const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};
